import { Component, inject, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup, FormsModule, ReactiveFormsModule, AbstractControl } from '@angular/forms';

import { TranslatePipe }  from '@ngx-translate/core'

import { DynamicForm, DynamicFormField } from '../../models/dynamic-form';
import { FormService } from '../../services/form-service';
import { UserService } from '../../services/user-service';
import { FormSpecialLogic } from '../../utils/form-special-logic/form-special-logic';

import { DynamicField } from './dynamic-field/dynamic-field';
import { NotificationService } from '../../services/notification-service';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import {MatButtonModule} from '@angular/material/button';

@Component( {
  selector: 'app-dynamic-form',
  imports: [
    DatePipe,
    TranslatePipe,
    FormsModule,
    ReactiveFormsModule,
    DynamicField,
    MatFormFieldModule,
    MatCardModule,
    MatButtonModule,
  ],
  templateUrl: './dynamic-form.html',
  styleUrl: './dynamic-form.css',
} )
export class DynamicFormComponent implements OnInit
{
  private route: ActivatedRoute = inject( ActivatedRoute );
  public pct: string | null = this.route.snapshot.paramMap.get( 'pct' );
  public formId: number | null = Number( this.route.snapshot.paramMap.get( 'formId' ) );
  public hubId: string | null = this.route.snapshot.paramMap.get( 'hubId' ) || null;
  public agencyId: number | null = Number( this.route.snapshot.paramMap.get( 'agencyId' ) ) || null;
  public clientId: string | null = this.route.snapshot.paramMap.get( 'clientId' ) || null;
  public visitId: string | null = this.route.snapshot.paramMap.get( 'visitId' ) || null;
  public mode: string | 'preview' | 'view' | 'create' | 'edit' = this.route.snapshot.paramMap.get( 'mode' ) || 'preview';
  public form: DynamicForm | undefined;
  public formGroup: FormGroup | undefined;
  public ready = () => ( this.formGroup?.valid && this.formGroup?.dirty && this.mode !== 'view' );

  constructor(
    private _user: UserService,
    private _router: Router,  
    private _formService: FormService,
    private _formEspecialLogic: FormSpecialLogic,
    private _notificationService: NotificationService
  )
  { }

  async ngOnInit(): Promise<void>
  {
    if ( !this.pct || !this.formId )
    {
      this._router.navigate( ['/main/client', this.clientId!] );      
      throw new Error( `Params: 'pct' and 'formId' are required.` );
    }
    // get form by mode
    switch ( this.mode )
    {
      case 'create':
        this.form = await this._formService.GetFormCreate( this.pct, this.formId, this.clientId! );       
        break;
      case 'view':
        if ( !this.clientId || !this.visitId )
        {
          // navitate back
          this._notificationService.showErrorMessage( `Params: 'clientId' and 'visitId' are required to load the form ${this.pct}${this.formId}.` );
          this._router.navigate( ['/main/client', this.clientId!] );
        }
        this.form = await this._formService.GetFormView( this.pct, this.formId, this.clientId!, this.visitId! );        
        break;
      case 'edit':
        if ( !this.clientId || !this.visitId )
        {
          // navitate back
          this._notificationService.showErrorMessage( `Params: 'clientId' and 'visitId' are required to load the form ${this.pct}${this.formId}.` );
          this._router.navigate( ['/main/client', this.clientId!] );
        }
        this.form = await this._formService.GetFormEdit( this.pct, this.formId, this.clientId!, this.visitId! );
        break;
      case 'preview':
      default:
        this.form = await this._formService.GetFormPreview( this.pct, this.formId, this.hubId, this.agencyId );
        break;
    }
    // create form group
    this.formGroup = await this._formService.GetFormGroup( this.form!.fields, this.mode );
        
    // subscribe to form changes
    if ( await this._formEspecialLogic.HasSpecialLogic( `${this.pct}${this.formId}` ) )
    {
      // initial execution
      await this._formEspecialLogic.Execute( `${this.pct}${this.formId}`, this.formGroup!.controls );

      // subscribe to changes
      this.formGroup.valueChanges.subscribe( async () =>
      {
        await this._formEspecialLogic.Execute( `${this.pct}${this.formId}`, this.formGroup!.controls );        
      } );
      
    }

  }

  getFormControl( field: DynamicFormField ): AbstractControl
  {
    const controlKey = `${field.name || field.fieldType}-${field.fieldId.toString()}`;
    return this.formGroup!.get( controlKey ) as AbstractControl;
  }

  async cancel(): Promise<void>
  {
    const backUrl = this._router.lastSuccessfulNavigation?.previousNavigation?.finalUrl;
    this._router.navigateByUrl( backUrl || `/main/client/summary/${this.clientId}` );
  }

  async save(): Promise<void>
  {        
    const form = await this.updateFormValues();
    const response = await this._formService.SaveForm( form );
    if ( response )
    {
      this._notificationService.showSuccessMessage( `${this.form?.displayName} saved successfully.` );
      this._router.navigate( ['/main/client/summary', this.clientId!] );
    }
  }

  async print(): Promise<void>
  {
    window.print();
  }

  async updateFormValues(): Promise<DynamicForm>
  {
    var updatedForm = {...this.form};
    if ( !this.form || !this.formGroup) return this.form!;
    var controlsKeys = Object.keys( this.formGroup.controls );
    controlsKeys.forEach( key =>
    {
      const _key = key.split( '-' )[0];
      updatedForm.fields?.filter( field => field.name === _key )
        .forEach( field =>
        {
          field.isSpecialControl ?
          field.value = this.formGroup!.controls[key].value :
          field.value = this.formGroup!.controls[key].value ? JSON.stringify( this.formGroup!.controls[key].value ) : ''
        } )
    } );

    return updatedForm as DynamicForm;
  }
}
